import { keyStore } from './keyStore'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createLocationChange,
  createModerationEvent,
  createPermitByAgencyType,
  createPermittingComment,
  deletePermitByAgencyType,
  getAllEngagementRecordings,
  getCameras,
  getEngagement,
  getEngagementActivities,
  getEngagementCameraRecording,
  getEngagementEvents,
  getEngagements,
  getLocation,
  getLocationAlarm,
  getLocationAlarms,
  getLocationAlarmSmsIncident,
  getLocationChanges,
  getLocationDeltas,
  getMediatorAccessToken,
  GetMediatorAccessTokenParams,
  getModerationEvents,
  getMonitoringCertificateByLocationId,
  getMonitoringServiceProviderLocation,
  getPermitHistory,
  getPermittingComments,
  getProvisioningWorkflowsByStatus,
  getRecordingSegment,
  getRecordingSegmentDetails,
  getRecordingUrl,
  getSafewords,
  getSecuritasIdentifiers,
  getSidFromCameraId,
  getSidFromSystemNumber,
  getSpecialist,
  getStreamingUrlFromMediator,
  patchModerationEvent,
  saveAddress,
  togglePracticeMode,
  triggerSendingOfCertificateEmail,
  updateContacts,
  updateDispatchNotes,
  updateLocationChange,
  updatePermitByAgencyType,
  updateSafeword,
  updateSecuritasIdentifiers,
  updateTemplateId,
  validateAddress
} from './api'
import {
  Address,
  AddressValidationRequest,
  Contacts,
  CreateLocationChange,
  CreatePermitRequest,
  DispatchNotesDto,
  EmailAddresses,
  GetAlarmsParams,
  GetEngagementsParams,
  GetLocationDeltasParams,
  GetMediatorUrlParams,
  LocationPermittingCommentRequest,
  ModerationEvent,
  ModerationEventSourceDto,
  PatchSecuritasLocationIdentifiers,
  PutTemplateIdDto,
  RecordingSegmentMetadata,
  RecordingType,
  UpdateLocationChange,
  UpdateSafewordRequest,
  WorkflowStatus
} from './models'
import * as _ from 'lodash'
import { mutationErrorHandler } from './apiUtils'
import { useSnackbar } from '../hooks/useSnackbar'
import { useCallback } from 'react'
import { eventWithTime } from '@rrweb/types'
import { LoggerName, namedLogger } from '../utils/logger'

const logger = namedLogger(LoggerName.apiQueries)

export const useLocation = (sid: string | undefined) =>
  useQuery({
    ...keyStore.locations.byId(sid!),
    queryFn: () => getLocation(sid!),
    enabled: !!sid,
    meta: { errorsToThrow: [404] }
  })

export const useChangelog = (sid: string | undefined) =>
  useQuery({
    ...keyStore.permits.history(sid!),
    queryFn: () => getPermitHistory(sid!),
    enabled: !!sid,
    meta: { errorsToThrow: [404] }
  })

export const usePermittingComments = (sid: string | undefined) =>
  useQuery({
    ...keyStore.permits.notes(sid!),
    queryFn: async () => {
      const comments = await getPermittingComments(sid!)
      return _.orderBy(comments, ['updatedAt'], ['desc'])
    },
    enabled: !!sid,
    meta: { errorsToThrow: [404] }
  })

export function useCreatePermittingComment() {
  const queryClient = useQueryClient()
  const snackbarContext = useSnackbar()
  return useMutation({
    mutationFn: ({ locationId, request }: { locationId: string; request: LocationPermittingCommentRequest }) =>
      createPermittingComment(locationId, request),
    onSuccess: async (_, { locationId }) => {
      await queryClient.invalidateQueries(keyStore.permits.notes(locationId))
    },
    ...mutationErrorHandler(snackbarContext, { errorMessage: 'Note could not be added. Please try again later.' })
  })
}

export function useCreatePermitByAgencyType() {
  const queryClient = useQueryClient()
  const snackbarContext = useSnackbar()
  return useMutation({
    mutationFn: ({ locationId, agencyType, request }: { locationId: string; agencyType: string; request: CreatePermitRequest }) =>
      createPermitByAgencyType(locationId, agencyType, request),
    onSuccess: async (_, { locationId }) => {
      await queryClient.invalidateQueries(keyStore.locations.byId(locationId))
      await queryClient.invalidateQueries(keyStore.permits.history(locationId))
    },
    ...mutationErrorHandler(snackbarContext, { errorMessage: 'Permit could not be added. Please try again later.' })
  })
}

export function useUpdatePermitByAgencyType() {
  const queryClient = useQueryClient()
  const snackbarContext = useSnackbar()
  return useMutation({
    mutationFn: ({ locationId, agencyType, request }: { locationId: string; agencyType: string; request: CreatePermitRequest }) =>
      updatePermitByAgencyType(locationId, agencyType, request),
    onSuccess: async (_, { locationId }) => {
      await queryClient.invalidateQueries(keyStore.locations.byId(locationId))
      await queryClient.invalidateQueries(keyStore.permits.history(locationId))
    },
    ...mutationErrorHandler(snackbarContext, { errorMessage: 'Changes could not be saved. Please try again later.' })
  })
}

export function useDeletePermitByAgencyType() {
  const queryClient = useQueryClient()
  const snackbarContext = useSnackbar()
  return useMutation({
    mutationFn: ({ locationId, agencyType }: { locationId: string; agencyType: string }) => deletePermitByAgencyType(locationId, agencyType),
    onSuccess: async (_, { locationId }) => {
      await queryClient.invalidateQueries(keyStore.locations.byId(locationId))
      await queryClient.invalidateQueries(keyStore.permits.history(locationId))
    },
    ...mutationErrorHandler(snackbarContext, { errorMessage: 'Permit could not be deleted. Please try again later.' })
  })
}

export function useSystemNumberToSidFn() {
  const queryClient = useQueryClient()
  return (systemNumber: string) =>
    queryClient.fetchQuery({
      ...keyStore.locations.getSidBySystemNumber(systemNumber),
      queryFn: () => getSidFromSystemNumber(systemNumber),
      meta: { errorsToThrow: '*' }
    })
}

export function useCameraIdToSid() {
  const queryClient = useQueryClient()
  return (cameraId: string) =>
    queryClient.fetchQuery({
      ...keyStore.locations.getSidByCameraId(cameraId),
      queryFn: () => getSidFromCameraId(cameraId),
      meta: { errorsToThrow: '*' }
    })
}

export const useLocationAlarms = (sid: string | undefined, queryParams?: GetAlarmsParams) =>
  useQuery({
    ...keyStore.locationAlarms.all(sid!),
    queryFn: async () => {
      const alarms = await getLocationAlarms(sid!, queryParams)
      alarms.items = _.orderBy(alarms.items, ['createdTimestamp'], ['desc'])
      return alarms
    },
    enabled: !!sid
  })

export const useLocationAlarm = (sid: string | undefined, alarmId: string | undefined) =>
  useQuery({
    ...keyStore.locationAlarms.byId(sid!, alarmId!),
    queryFn: () => getLocationAlarm(sid!, alarmId!),
    enabled: !!sid && !!alarmId
  })

export const useAlarmSmsIncident = (sid: string | undefined, alarmId: string | undefined) =>
  useQuery({
    ...keyStore.locationAlarms.smsIncident(sid!, alarmId!),
    queryFn: () => getLocationAlarmSmsIncident(sid!, alarmId!),
    enabled: !!sid && !!alarmId
  })

export const useLocationChanges = (sid: string | undefined) =>
  useQuery({
    ...keyStore.locations.changes(sid!),
    queryFn: () => getLocationChanges(sid!),
    enabled: !!sid
  })

export const useLocationDeltas = (sid: string | undefined, params?: GetLocationDeltasParams) =>
  useQuery({
    ...keyStore.locations.deltas(sid!, params),
    queryFn: () => getLocationDeltas(sid!, params),
    enabled: !!sid
  })

export const useValidateAddressFn = () => {
  const queryClient = useQueryClient()
  return useCallback(
    (sid: string, address: AddressValidationRequest) =>
      queryClient.fetchQuery({
        ...keyStore.validatedAddresses.byAddress(sid, address),
        queryFn: () => validateAddress(address)
      }),
    [queryClient]
  )
}

export const useCreateLocationChange = () => {
  const snackbarContext = useSnackbar()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ locationId, locationChange }: { locationId: string; locationChange: CreateLocationChange }) =>
      createLocationChange(locationId, locationChange),
    onSuccess: async (_, { locationId }) => {
      await queryClient.invalidateQueries(keyStore.locations.changes(locationId))
    },
    ...mutationErrorHandler(snackbarContext, { errorMessage: 'Changes could not be saved. Please try again later.' })
  })
}

export const useUpdateLocationChange = () => {
  const snackbarContext = useSnackbar()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      locationId,
      locationChangeId,
      locationChange
    }: {
      locationId: string
      locationChangeId: string
      locationChange: UpdateLocationChange
    }) => updateLocationChange(locationId, locationChangeId, locationChange),
    onSuccess: async (_, { locationId }) => {
      await queryClient.invalidateQueries(keyStore.locations.changes(locationId))
    },
    ...mutationErrorHandler(snackbarContext, { errorMessage: 'Changes could not be saved. Please try again later.' })
  })
}

export type RecordingSegments = {
  segments: RecordingSegment[]
  recordingType: RecordingType
}

export type RecordingSegment = RecordingSegmentMetadata & {
  data: Blob
  rrwebEvents?: eventWithTime[]
}

/**
 * Hook to fetch recording for an engagement
 * @param engagementId ID of the engagement to fetch recording for
 * @param recordingType
 * @returns recording segments query result
 */
export const useRecordingSegments = (engagementId: string | undefined, recordingType: RecordingType) =>
  useQuery({
    ...keyStore.recordings.segmentsByEngagementAndType(engagementId!, recordingType),
    enabled: !!engagementId,
    queryFn: async () => {
      const segmentDetails: RecordingSegmentMetadata[] = await getRecordingSegmentDetails(engagementId!, recordingType)

      const segmentPromises: Promise<Blob>[] = []
      for (const segmentDetail of segmentDetails) {
        const segmentPromise: Promise<Blob> = getRecordingSegment(engagementId!, recordingType, segmentDetail.segmentId)
        segmentPromises.push(segmentPromise)
        segmentPromise.catch(e => logger.error('Recording segment fetch rejected', e as Error))
      }
      const segmentData = await Promise.all(segmentPromises)

      const recordingSegments: RecordingSegment[] = []
      for (let i = 0; i < segmentDetails.length; i++) {
        recordingSegments.push({ ...segmentDetails[i], data: segmentData[i] })
      }

      return { segments: recordingSegments, recordingType } satisfies RecordingSegments
    }
  })

export const useRecordingUrlFn = () => {
  const queryClient = useQueryClient()
  return useCallback(
    (parms: GetMediatorUrlParams) =>
      queryClient.fetchQuery({
        ...keyStore.rrweb.recordingUrl(parms),
        queryFn: () => getRecordingUrl(parms),
        meta: { errorsToThrow: '*' }
      }),
    [queryClient]
  )
}

export const useGetMediatorAccessTokenFn = () => {
  const queryClient = useQueryClient()
  return useCallback(
    (params: GetMediatorAccessTokenParams) =>
      queryClient.fetchQuery({
        ...keyStore.rrweb.mediatorAccessToken(params),
        queryFn: () => getMediatorAccessToken(params),
        meta: { errorsToThrow: '*' },
        gcTime: 60 * 60 * 1000, // 60 minutes
        staleTime: 55 * 60 * 1000 // 55 minutes
      }),
    [queryClient]
  )
}

export const useGetStreamingUrlFn = () => {
  const queryClient = useQueryClient()
  return useCallback(
    (mediatorUrl: string, accessToken: string) =>
      queryClient.fetchQuery({
        ...keyStore.rrweb.streamingSessionUrl(mediatorUrl),
        queryFn: () => getStreamingUrlFromMediator(mediatorUrl, accessToken),
        meta: { errorsToThrow: '*' },
        gcTime: 5 * 60 * 1000, // 5 minutes
        staleTime: 3 * 60 * 1000 // 3 minutes
      }),
    [queryClient]
  )
}

export const useModerationEvents = (sid: string | undefined) =>
  useQuery({
    ...keyStore.moderationEvents.all(sid!),
    queryFn: () => getModerationEvents(sid!),
    enabled: !!sid,
    meta: { errorsToThrow: [404] }
  })

export function useCreateModerationEvent() {
  const queryClient = useQueryClient()
  const snackbarContext = useSnackbar()

  return useMutation({
    mutationFn: ({ locationId, moderationEventSource }: { locationId: string; moderationEventSource: ModerationEventSourceDto }) =>
      createModerationEvent(locationId, moderationEventSource),
    onSuccess: async (_, { locationId }) => {
      await queryClient.invalidateQueries(keyStore.moderationEvents.all(locationId))
    },
    ...mutationErrorHandler(snackbarContext, {
      errorMessage: 'Moderation event could not be created. Please try again later.'
    })
  })
}

export function usePatchModerationEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (moderationEvent: ModerationEvent) => patchModerationEvent(moderationEvent),
    onSuccess: async (_, moderationEvent) => {
      await queryClient.invalidateQueries(keyStore.moderationEvents.all(moderationEvent.locationId))
      await queryClient.invalidateQueries(keyStore.locations.cameras(moderationEvent.locationId))
    }
  })
}

export function useLocationCameras(locationId: string | undefined, enabled = true) {
  return useQuery({
    ...keyStore.locations.cameras(locationId!),
    queryFn: () => getCameras(locationId!),
    meta: { errorMessage: 'Unable to load camera details for location.' },
    enabled: !!locationId && enabled
  })
}

export function useEngagement(engagementId: string | undefined, enabled = true) {
  return useQuery({
    ...keyStore.engagements.byId(engagementId!),
    queryFn: () => getEngagement(engagementId!),
    meta: { errorMessage: 'Unable to load engagement details.', errorsToThrow: [404] },
    enabled: !!engagementId && enabled
  })
}

export function useEngagements(queryParams?: GetEngagementsParams) {
  return useQuery({
    ...keyStore.engagements.search(queryParams),
    queryFn: () => getEngagements(queryParams),
    meta: { errorMessage: 'Unable to load engagement search results.' }
  })
}

export function useEngagementEvents(engagementId: string | undefined, enabled = true) {
  return useQuery({
    ...keyStore.engagements.events(engagementId!),
    queryFn: () => getEngagementEvents(engagementId!),
    meta: { errorMessage: 'Unable to load engagement events.' },
    enabled: !!engagementId && enabled
  })
}

export function useEngagementActivities(engagementId: string | undefined, enabled = true) {
  return useQuery({
    ...keyStore.engagements.activities(engagementId!),
    queryFn: () => getEngagementActivities(engagementId!) ?? [],
    meta: { errorMessage: 'Unable to load engagement activities.' },
    enabled: !!engagementId && enabled
  })
}

export function useMonitoringServiceProviderLocation(locationId: string) {
  return useQuery({
    ...keyStore.mspLocation.byId(locationId!),
    queryFn: () => getMonitoringServiceProviderLocation(locationId!),
    meta: { errorMessage: 'Unable to load monitoring service provider location details' },
    enabled: !!locationId
  })
}

export function useSafewords(locationId: string, enabled = true) {
  return useQuery({
    ...keyStore.locations.safewords(locationId),
    queryFn: () => getSafewords(locationId),
    meta: { errorMessage: 'Unable to load safe word results.' },
    enabled
  })
}

export function useUpdateSafeword() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (req: UpdateSafewordRequest & { locationId: string; safewordId: string }) =>
      updateSafeword(req.locationId, req.safewordId, req),
    onSuccess: async (_, { locationId }) => {
      await queryClient.invalidateQueries(keyStore.locations.byId(locationId))
      await queryClient.invalidateQueries(keyStore.locations.safewords(locationId))
    }
  })
}

export function useUpdateTemplateId(locationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (req: PutTemplateIdDto) => updateTemplateId(locationId, req),
    onSuccess: async () => {
      await queryClient.invalidateQueries(keyStore.locations.byId(locationId))
    }
  })
}

export function useSecuritasIdentifiersFn() {
  const queryClient = useQueryClient()
  return (locationId: string) =>
    queryClient.fetchQuery({
      ...keyStore.locations.securitasIdentifiers(locationId),
      queryFn: () => getSecuritasIdentifiers(locationId),
      meta: { errorsToThrow: '*' }
    })
}

export function useUpdateSecuritasIdentifiers(locationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (req: PatchSecuritasLocationIdentifiers) => updateSecuritasIdentifiers(locationId, req),
    onSuccess: async () => {
      await queryClient.invalidateQueries(keyStore.locations.securitasIdentifiers(locationId))
      await queryClient.invalidateQueries(keyStore.locations.byId(locationId))
    }
  })
}

export function useUpdateDispatchNotes(locationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (req: DispatchNotesDto) => updateDispatchNotes(locationId, req),
    onSuccess: async () => {
      await queryClient.invalidateQueries(keyStore.locations.byId(locationId))
    }
  })
}

export function useUpdateContacts(locationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (contacts: Contacts) => updateContacts(locationId, contacts),
    onSuccess: async () => {
      await queryClient.invalidateQueries(keyStore.locations.byId(locationId))
    }
  })
}

export function useTogglePracticeMode() {
  const queryClient = useQueryClient()
  const snackbarContext = useSnackbar()
  return useMutation({
    mutationFn: ({
      locationId,
      practiceModeEnabled,
      practiceModeEndDate
    }: {
      locationId: string
      practiceModeEnabled: boolean
      practiceModeEndDate: string | undefined
    }) =>
      togglePracticeMode(locationId, {
        serviceStatus: {
          practiceMode: {
            isEnabled: practiceModeEnabled,
            ...(practiceModeEndDate && { endTimestamp: practiceModeEndDate })
          }
        }
      }),
    onSuccess: async (_, { locationId }) => {
      await queryClient.invalidateQueries(keyStore.practiceMode.status(locationId))
      await queryClient.invalidateQueries(keyStore.locations.byId(locationId))
    },
    ...mutationErrorHandler(snackbarContext, { errorMessage: 'Changes could not be saved. Please try again later.' })
  })
}

export function useTriggerSendingOfCertificateEmailFn() {
  const queryClient = useQueryClient()
  return (locationId: string, request: EmailAddresses) =>
    queryClient.fetchQuery({
      ...keyStore.certificates.email(locationId),
      queryFn: () => triggerSendingOfCertificateEmail(locationId, request),
      meta: { errorsToThrow: '*' }
    })
}

export function useGetMonitoringCertificateFn() {
  const queryClient = useQueryClient()
  return (locationId: string) =>
    queryClient.fetchQuery({
      ...keyStore.certificates.byId(locationId),
      queryFn: () => getMonitoringCertificateByLocationId(locationId),
      meta: { errorsToThrow: '*' }
    })
}

export function useSaveAddress() {
  const queryClient = useQueryClient()
  const snackbarContext = useSnackbar()
  return useMutation({
    mutationFn: ({ locationId, address }: { locationId: string; address: Address }) => saveAddress(locationId, address),
    onSuccess: async (_, { locationId }) => {
      await queryClient.invalidateQueries(keyStore.locations.byId(locationId))
    },
    ...mutationErrorHandler(snackbarContext, { errorMessage: 'Changes could not be saved. Please try again later.' })
  })
}

export function useGetWorkFlowsByStatusFn() {
  const queryClient = useQueryClient()
  return useCallback(
    (locationId: string, workflowStatus: WorkflowStatus) =>
      queryClient.fetchQuery({
        ...keyStore.locations.workFlowsByStatus(locationId, workflowStatus),
        queryFn: () => getProvisioningWorkflowsByStatus(locationId, workflowStatus),
        meta: { errorsToThrow: '*' },

        // cache for less than the polling interval of 5 seconds to ensure we get fresh data on each poll
        // but also prevent unnecessary requests if the component re-renders for other reasons
        gcTime: 2500
      }),
    [queryClient]
  )
}

export function useSpecialist(monitoringServiceProviderId: string | undefined, specialistId: string | undefined, enabled = true) {
  const specialistQuery = getSpecialistQueryOptions(monitoringServiceProviderId ?? '', specialistId ?? '')

  return useQuery({
    ...specialistQuery,
    enabled: !!monitoringServiceProviderId && !!specialistId && enabled
  })
}

function getSpecialistQueryOptions(monitoringServiceProviderId: string, specialistId: string) {
  return {
    ...keyStore.specialists.byId(monitoringServiceProviderId, specialistId),
    queryFn: () => getSpecialist(monitoringServiceProviderId, specialistId),
    meta: { errorMessage: 'Unable to load specialist data.' }
  }
}

export function useSpecialists(specialists: { monitoringServiceProviderId: string; specialistId: string }[], enabled = true) {
  return useQueries({
    queries: specialists.map(({ monitoringServiceProviderId, specialistId }) => ({
      ...getSpecialistQueryOptions(monitoringServiceProviderId, specialistId),
      enabled
    }))
  })
}

export function useEngagementRecordings(params: GetMediatorAccessTokenParams | undefined, enabled = true) {
  return useQuery({
    ...keyStore.engagements.recordings(params!),
    queryFn: () => getAllEngagementRecordings(params!.locationId, params!.engagementId),
    meta: { errorMessage: 'Unable to load engagement recordings.' },
    enabled: !!params?.locationId && !!params?.engagementId && enabled
  })
}

export function useCameraRecording(params: GetMediatorUrlParams | undefined, enabled = true) {
  return useQuery({
    ...keyStore.engagements.cameraRecordings(params!),
    queryFn: () => getEngagementCameraRecording(params!.locationId, params!.engagementId, params!.cameraId, params!.start, params!.end),
    meta: { errorMessage: 'Unable to load camera recordings.' },
    enabled: !!params?.locationId && !!params?.engagementId && !!params?.cameraId && !!params?.start && !!params?.end && enabled
  })
}
